import { useInfiniteQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConnectionsService } from '../../../../../js/api';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { Heading } from '../../../components/heading';
import { ListErrorState } from '../../../components/list-error-state';
import { ListFilter } from '../../../components/list-filter';
import { QueueList } from '../../../components/queue-list';
import { ReferrerBackLink } from '../../../components/referrer-back-link';
import { StatusPill } from '../../../components/StatusPill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortHeader,
} from '../../../components/table';
import { QueueCodes, WorkflowScopes } from '../../../constants/workflows';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { getDateTemporalityClass } from '../../../utils/date';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import {
  MAIN_CONTENT_AREA,
  MAIN_CONTENT_AREA_FIXED,
  PAGE_HEADER,
  TABLE_SCROLL_BOX,
} from '../../../utils/layoutStyles';
import { TRANSITION_ALL } from '../../../utils/transitionStyles';
import ClearanceCard from '../../Organization/Clearance/ClearanceCard';
import { ClearanceDrawer } from '../../Organization/Clearance/ClearanceDrawer';

const DATE_FORMAT = 'LLL d, yyyy';
const PAGE_SIZE = 20;

type ApiParams = Parameters<typeof ConnectionsService.connectionsClearanceList>[0];

const CATEGORY_TO_API_PARAM: Record<string, keyof ApiParams> = {};

const CATEGORIES = Object.keys(CATEGORY_TO_API_PARAM) as readonly string[];

export type ClearancesListPageProps = {
  title?: string;
  queryKeyBase: string;
  displayTypePrefKey: string;
  showOrganization: boolean;
  showOrganizationFilter?: boolean;
  queueCategory: string;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ClearancesListPage: React.FC<ClearancesListPageProps> = ({
  title = 'Clearances',
  queryKeyBase,
  displayTypePrefKey,
  showOrganization,
  showOrganizationFilter = false,
  queueCategory,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  useDocumentTitle(title);
  const [searchParams, setSearchParams] = useSearchParams();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const selectedQueueCode: string | undefined = searchParams.get('queue') || undefined;

  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    categories: CATEGORIES,
    displayTypePrefKey,
  });

  const search = filterValues.search || '';
  const statuses = filterValues.statuses || [];
  const reasons = filterValues.reasons || [];
  const categoryFilters = useMemo(() => filterValues.categories || {}, [filterValues.categories]);
  const healthcareEntity = filterValues.healthcare_entity || '';

  const handleQueueChange = (queueCode: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (queueCode) {
      newSearchParams.set('queue', queueCode);
    } else {
      newSearchParams.delete('queue');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  const apiQueueCode = selectedQueueCode || undefined;

  const displayType = (filterValues.displayType || 'table') as 'card' | 'table';
  // Table view: the rows scroll in their own box under a sticky header (utils/layoutStyles).
  const tableScrolls = displayType !== 'card';
  const ordering = (filterValues.ordering || '-updated_at') as string;

  const setOrdering = useCallback(
    (next: string) => {
      updateFilters({
        search: filterValues.search,
        statuses: filterValues.statuses,
        reasons: filterValues.reasons,
        categories: filterValues.categories,
        displayType,
        ordering: next,
        healthcare_entity: filterValues.healthcare_entity,
      });
    },
    [
      updateFilters,
      filterValues.search,
      filterValues.statuses,
      filterValues.reasons,
      filterValues.categories,
      filterValues.healthcare_entity,
      displayType,
    ]
  );

  const categoryApiParams = useMemo(() => {
    const params: Partial<ApiParams> = {};
    Object.entries(categoryFilters).forEach(([category, values]) => {
      const apiParamName = CATEGORY_TO_API_PARAM[category];
      if (apiParamName && values.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (params as Record<string, unknown>)[apiParamName] = values;
      }
    });
    return params;
  }, [categoryFilters]);

  const queryKey = useMemo(
    () => [
      queryKeyBase,
      selectedQueueCode,
      search,
      statuses,
      reasons,
      categoryFilters,
      ordering,
      healthcareEntity,
    ],
    [
      queryKeyBase,
      selectedQueueCode,
      search,
      statuses,
      reasons,
      categoryFilters,
      ordering,
      healthcareEntity,
    ]
  );

  const {
    data,
    isLoading: isLoadingClearances,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      return ConnectionsService.connectionsClearanceList({
        // @ts-expect-error - queue parameter supported by backend but may not be in generated types
        queue: apiQueueCode,
        search: search || undefined,
        status: statuses.length > 0 ? (statuses as Array<string | null>) : undefined,
        reason: reasons.length > 0 ? (reasons as Array<string | null>) : undefined,
        healthcareEntity: healthcareEntity || undefined,
        ...categoryApiParams,
        ordering: ordering as ApiParams['ordering'],
        limit: PAGE_SIZE,
        offset: pageParam as number,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) {
        return allPages.length * PAGE_SIZE;
      }
      return undefined;
    },
    enabled: !!selectedQueueCode,
  });

  const clearances = useMemo(() => data?.pages.flatMap((page) => page.results || []) || [], [data]);
  const totalCount = data?.pages[0]?.count || 0;

  const loadMoreClearances = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreClearances, hasNextPage || false, {
    enabled: hasNextPage && !isLoadingClearances,
  });

  useEffect(() => {
    const clearanceIdFromUrl = searchParams.get('id');
    if (clearanceIdFromUrl) {
      setIsDrawerOpen(true);
    } else if (isDrawerOpen) {
      setIsDrawerOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleRowClick = useCallback(
    (clearanceId: string) => {
      setIsDrawerOpen(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('id', clearanceId);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('id');
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const selectedIdFromUrl = searchParams.get('id');

  const selectedIndex = useMemo(() => {
    if (!selectedIdFromUrl) return -1;
    return clearances.findIndex((c) => String(c.id) === String(selectedIdFromUrl));
  }, [clearances, selectedIdFromUrl]);

  const prevClearanceId: string | null = useMemo(() => {
    if (selectedIndex > 0) return clearances[selectedIndex - 1]?.id ?? null;
    return null;
  }, [clearances, selectedIndex]);

  const nextClearanceId: string | null = useMemo(() => {
    if (selectedIndex >= 0 && selectedIndex < clearances.length - 1)
      return clearances[selectedIndex + 1]?.id ?? null;
    return null;
  }, [clearances, selectedIndex]);

  const neighborsByIdRef = useRef<Map<string, { prev: string | null; next: string | null }>>(
    new Map()
  );

  const initialIdRef = useRef<string | null>(searchParams.get('id'));

  useEffect(() => {
    if (!selectedIdFromUrl) return;
    if (selectedIndex >= 0) {
      neighborsByIdRef.current.set(String(selectedIdFromUrl), {
        prev: prevClearanceId ?? null,
        next: nextClearanceId ?? null,
      });
    }
  }, [selectedIdFromUrl, selectedIndex, prevClearanceId, nextClearanceId]);

  useEffect(() => {
    if (!selectedQueueCode || selectedQueueCode === 'all') return;
    if (!selectedIdFromUrl) return;

    if (initialIdRef.current && selectedIdFromUrl === initialIdRef.current) {
      return;
    }

    if (selectedIndex === -1 && !isLoadingClearances) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('id');
      nextParams.delete('tab');
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    selectedQueueCode,
    selectedIdFromUrl,
    selectedIndex,
    isLoadingClearances,
    searchParams,
    setSearchParams,
  ]);

  const sortOptions = useMemo(
    () => [
      { label: 'Last Updated', value: 'updated_at' },
      { label: 'Created', value: 'created_at' },
      ...(showOrganization ? [{ label: 'Organization', value: 'healthcare_entity__name' }] : []),
      { label: 'Next Review On', value: 'next_review_on' },
      { label: 'Clinician', value: 'provider_display_name' },
      { label: 'Status', value: 'current_status__title' },
      { label: 'Reason', value: 'current_reason__title' },
    ],
    [showOrganization]
  );

  return (
    <div className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden">
      <div
        className={clsx(
          `tw:flex tw:flex-col ${TRANSITION_ALL} tw:overflow-hidden`,
          isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
        )}
      >
        <main className={clsx(MAIN_CONTENT_AREA, tableScrolls && MAIN_CONTENT_AREA_FIXED)}>
          <div className={STICKY_HEADER}>
            <ReferrerBackLink />
            <div className={PAGE_HEADER}>
              <Heading level={1} className="tw:!text-brand-green-0">
                <i className="fas fa-fw fa-check-double" />
                {title}
              </Heading>
            </div>

            <div>
              <QueueList
                categoryName={queueCategory}
                notificationScope={WorkflowScopes.Clearance}
                selectedQueue={selectedQueueCode || undefined}
                priorityQueues={true}
                priorityQueueList={[QueueCodes.READY_FOR_CLEARANCE]}
                onQueueChange={handleQueueChange}
              />
            </div>
          </div>

          <div className="tw:mt-1">
            <ListFilter
              show_search={true}
              workflow_scope={WorkflowScopes.Clearance}
              categories={Array.from(CATEGORIES)}
              showStatus={true}
              showReason={true}
              showOrganizationFilter={showOrganizationFilter}
              showDisplayType={true}
              values={filterValues}
              onFilterChange={updateFilters}
              sortOptions={sortOptions}
            />
          </div>

          <div className={clsx('tw:mt-6', tableScrolls && TABLE_SCROLL_BOX)}>
            {isError ? (
              <ListErrorState testId="clearances-error" />
            ) : isLoadingClearances && clearances.length === 0 ? (
              <div className="tw:text-center tw:py-8">Loading clearances...</div>
            ) : clearances.length === 0 ? (
              <div className="tw:text-center tw:text-zinc-500 tw:py-8">
                No clearances found
                <div className="tw:text-zinc-500 tw:italic tw:text-sm">
                  Refine your search or check back later
                </div>
              </div>
            ) : (
              <>
                {clearances.length > 0 && (
                  <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                    Showing {clearances.length} of {totalCount} clearances
                  </div>
                )}

                {displayType === 'card' ? (
                  <div className="tw:@container">
                    <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:mt-2 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                      {clearances.map((clearance) => {
                        const idFromUrl = searchParams.get('id');
                        const isSelected = clearance.id === idFromUrl;
                        return (
                          <button
                            key={clearance.id}
                            type="button"
                            onClick={() => handleRowClick(clearance.id)}
                            className="tw:block tw:w-full tw:text-left"
                          >
                            <ClearanceCard
                              clearance={clearance}
                              isSelected={isSelected}
                              showOrganization={showOrganization}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <Table striped dense stickyHeader={tableScrolls}>
                    <TableHead>
                      <TableSortHeader
                        sortKey="provider_display_name"
                        ordering={ordering}
                        onOrderingChange={setOrdering}
                      >
                        Clinician
                      </TableSortHeader>
                      {showOrganization && (
                        <TableSortHeader
                          sortKey="healthcare_entity__name"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Organization
                        </TableSortHeader>
                      )}
                      <TableSortHeader
                        sortKey="current_status__title"
                        ordering={ordering}
                        onOrderingChange={setOrdering}
                      >
                        Status
                      </TableSortHeader>
                      {selectedQueueCode === 'all' && (
                        <TableSortHeader
                          sortKey="current_reason__title"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Reason
                        </TableSortHeader>
                      )}
                      {selectedQueueCode === 'all' && (
                        <TableSortHeader
                          sortKey="next_review_on"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Next Review
                        </TableSortHeader>
                      )}
                      <TableSortHeader
                        sortKey="updated_at"
                        ordering={ordering}
                        onOrderingChange={setOrdering}
                      >
                        Last Updated
                      </TableSortHeader>
                      <TableSortHeader
                        sortKey="created_at"
                        ordering={ordering}
                        onOrderingChange={setOrdering}
                      >
                        Created Date
                      </TableSortHeader>
                    </TableHead>
                    <TableBody>
                      {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
                      {clearances.map((clearance) => {
                        const idFromUrl = searchParams.get('id');
                        const isSelected = clearance.id === idFromUrl;
                        return (
                          <TableRow
                            key={clearance.id}
                            onClick={() => handleRowClick(clearance.id)}
                            className={clsx(isSelected ? 'tw:!bg-brand-green-2' : '')}
                          >
                            <TableCell>{clearance.provider.display_name}</TableCell>
                            {showOrganization && (
                              <TableCell>{clearance.healthcare_entity?.name || '--'}</TableCell>
                            )}
                            <TableCell>
                              <StatusPill
                                scope={WorkflowScopes.Clearance}
                                status={clearance.current_status}
                              />
                            </TableCell>
                            {selectedQueueCode === 'all' && (
                              <TableCell>{clearance.current_reason?.title || '--'}</TableCell>
                            )}
                            {selectedQueueCode === 'all' && (
                              <TableCell>
                                {clearance.next_review_on ? (
                                  <span
                                    className={getDateTemporalityClass(clearance.next_review_on)}
                                  >
                                    {format(parseISO(clearance.next_review_on), DATE_FORMAT)}
                                  </span>
                                ) : (
                                  '--'
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              {clearance.updated_at
                                ? format(parseISO(clearance.updated_at), DATE_FORMAT)
                                : '--'}
                            </TableCell>
                            <TableCell>
                              {clearance.created_at
                                ? format(parseISO(clearance.created_at), DATE_FORMAT)
                                : '--'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}

                {hasNextPage && (
                  <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                    {isFetchingNextPage && (
                      <div className="tw:text-center tw:text-zinc-500">
                        Loading more clearances...
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
        <DrawerBody className="tw:!text-sm/6">
          {selectedIdFromUrl && (
            <ClearanceDrawer
              key={selectedIdFromUrl}
              clearanceId={selectedIdFromUrl}
              prevId={(() => {
                const cached = selectedIdFromUrl
                  ? neighborsByIdRef.current.get(String(selectedIdFromUrl))
                  : undefined;
                return selectedIndex >= 0 ? prevClearanceId : (cached?.prev ?? null);
              })()}
              nextId={(() => {
                const cached = selectedIdFromUrl
                  ? neighborsByIdRef.current.get(String(selectedIdFromUrl))
                  : undefined;
                return selectedIndex >= 0 ? nextClearanceId : (cached?.next ?? null);
              })()}
              selectedQueueCode={selectedQueueCode}
              onClose={handleCloseDrawer}
              listQueryKeyBase={queryKeyBase}
            />
          )}
        </DrawerBody>
      </Drawer>
    </div>
  );
};
